import type { Milestone, StudyLog } from './index';
import { calculateRoundProgress } from './progress';

export const MILESTONE_CATALOG_VERSION = 1 as const;

export const MILESTONE_CATALOG = [
  { kind: 'daily_stamp', title: '오늘의 도장', threshold: 1 },
  { kind: 'streak_3', title: '3일 연속', threshold: 3 },
  { kind: 'streak_7', title: '7일 연속', threshold: 7 },
  { kind: 'round_completed', title: '회독 완주', threshold: 1 },
  { kind: 'plan_completed', title: '계획 완주', threshold: 1 },
  { kind: 'mastered_50', title: '오답 50개 정복', threshold: 50 },
  { kind: 'study_100h', title: '100시간 공부', threshold: 6_000 },
  { kind: 'room_3', title: '모각공 3회', threshold: 3 },
] as const;

export type MilestoneCatalogKind = (typeof MILESTONE_CATALOG)[number]['kind'];

export type MilestoneCandidate = {
  key: string;
  kind: MilestoneCatalogKind;
  examId?: Milestone['examId'];
  sourceEntityId?: Milestone['sourceEntityId'];
};

function dayNumber(value: string): number {
  return Date.parse(`${value}T00:00:00.000Z`) / 86_400_000;
}

function longestConsecutiveDays(values: readonly string[]): number {
  const days = [...new Set(values)].map(dayNumber).sort((left, right) => left - right);
  let longest = 0;
  let current = 0;
  let previous: number | undefined;
  for (const day of days) {
    current = previous !== undefined && day === previous + 1 ? current + 1 : 1;
    longest = Math.max(longest, current);
    previous = day;
  }
  return longest;
}

export function cumulativeMilestoneCandidates(options: {
  examId: NonNullable<Milestone['examId']>;
  logs: readonly StudyLog[];
  masteredNoteIds: readonly NonNullable<Milestone['sourceEntityId']>[];
  completedPlanIds: readonly NonNullable<Milestone['sourceEntityId']>[];
  roomSessionIds: readonly NonNullable<Milestone['sourceEntityId']>[];
}): MilestoneCandidate[] {
  const activeLogs = options.logs.filter(
    (log) =>
      log.examId === options.examId &&
      log.deletedAt === undefined &&
      log.source !== 'onboarding_import',
  );
  const candidates: MilestoneCandidate[] = [];
  const streak = longestConsecutiveDays(activeLogs.map((log) => log.studyDate));
  if (streak >= 3) candidates.push({ key: 'streak:3', kind: 'streak_3', examId: options.examId });
  if (streak >= 7) candidates.push({ key: 'streak:7', kind: 'streak_7', examId: options.examId });
  if (activeLogs.reduce((total, log) => total + (log.minutes ?? 0), 0) >= 6_000) {
    candidates.push({ key: 'study_minutes:6000', kind: 'study_100h', examId: options.examId });
  }
  if (options.masteredNoteIds.length >= 50) {
    candidates.push({ key: 'mastered_notes:50', kind: 'mastered_50', examId: options.examId });
  }
  for (const sourceEntityId of options.completedPlanIds) {
    candidates.push({
      key: 'plan_completed',
      kind: 'plan_completed',
      examId: options.examId,
      sourceEntityId,
    });
  }
  if (options.roomSessionIds.length >= 3) {
    candidates.push({ key: 'room_sessions:3', kind: 'room_3', examId: options.examId });
  }
  return candidates;
}

export function milestoneReconciliation(options: {
  existing: readonly Milestone[];
  expected: readonly MilestoneCandidate[];
}): { award: MilestoneCandidate[]; revoke: Milestone[] } {
  const expectedIdentities = new Set(
    options.expected.map((candidate) =>
      [candidate.key, candidate.examId ?? '-', candidate.sourceEntityId ?? '-'].join(':'),
    ),
  );
  const existingIdentities = new Set(options.existing.map(milestoneIdentity));
  return {
    award: options.expected.filter(
      (candidate) =>
        !existingIdentities.has(
          [candidate.key, candidate.examId ?? '-', candidate.sourceEntityId ?? '-'].join(':'),
        ),
    ),
    revoke: options.existing.filter(
      (milestone) =>
        [
          'streak:3',
          'streak:7',
          'study_minutes:6000',
          'mastered_notes:50',
          'plan_completed',
          'room_sessions:3',
        ].includes(milestone.key) && !expectedIdentities.has(milestoneIdentity(milestone)),
    ),
  };
}

export type StudyLogMilestoneDescriptor = {
  key: `daily_stamp:${string}` | `round_completed:${string}:${number}`;
  kind: 'daily_stamp' | 'round_completed';
  round?: number;
};

export function studyLogMilestoneDescriptors(
  log: StudyLog,
  logs: readonly StudyLog[],
  subjectTotalUnits: number,
): StudyLogMilestoneDescriptor[] {
  if (log.source === 'onboarding_import' || log.deletedAt !== undefined) return [];

  const descriptors: StudyLogMilestoneDescriptor[] = [
    {
      key: `daily_stamp:${log.studyDate}`,
      kind: 'daily_stamp',
    },
  ];
  const progress = calculateRoundProgress(logs, log.subjectId, log.round, subjectTotalUnits);
  if (progress.coveredUnits === progress.totalUnits) {
    descriptors.push({
      key: `round_completed:${log.subjectId}:${log.round}`,
      kind: 'round_completed',
      round: log.round,
    });
  }
  return descriptors;
}

export function milestoneIdentity(milestone: Milestone): string {
  return [milestone.key, milestone.examId ?? '-', milestone.sourceEntityId ?? '-'].join(':');
}

export function awardMilestone(
  existing: readonly Milestone[],
  candidate: Milestone,
): { milestones: Milestone[]; awarded: boolean } {
  const identity = milestoneIdentity(candidate);
  if (existing.some((milestone) => milestoneIdentity(milestone) === identity)) {
    return { milestones: [...existing], awarded: false };
  }
  return { milestones: [...existing, candidate], awarded: true };
}
